import { useDebouncedCallback } from "@judo/core";
import { useModelLabel } from "@judo/i18n";
import type { Input, NumericInput } from "@judo/model-api";
import { getInputTestId } from "@judo/test-ids";
import { TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { NumericFormat } from "react-number-format";
import { buildIconAdornment } from "../utils/icon-adornment";

export interface NumericInputComponentProps {
	element: NumericInput | Input;
	readOnly?: boolean;
	value?: number | null;
	onChange?: (value: number | null) => void;
	error?: string;
	disabled?: boolean;
	judoClassName?: string;
}

/**
 * Numeric input component using react-number-format.
 * Provides locale-aware formatting with debounced value changes.
 */
export function NumericInputComponent({
	element,
	readOnly,
	value,
	onChange,
	error,
	disabled,
	judoClassName,
}: NumericInputComponentProps) {
	const testId = getInputTestId(element);
	const { label } = useModelLabel(element);
	const numericElement = element as NumericInput;
	const startAdornment = buildIconAdornment(element.icon);

	const decimalScale = numericElement.scale ?? 0;

	// Local state for immediate display.
	//
	// Review refactor R1 — race-condition guard (see TextInputComponent for full
	// rationale). `isDirtyRef` prevents an external `value` update from clobbering
	// the user's in-flight typing before the debounced onChange has propagated.
	const [localValue, setLocalValue] = useState<number | null | undefined>(value);
	const isDirtyRef = useRef(false);

	useEffect(() => {
		if (!isDirtyRef.current) {
			setLocalValue(value);
		}
	}, [value]);

	const debouncedOnChange = useDebouncedCallback((newValue: number | null) => {
		onChange?.(newValue);
		isDirtyRef.current = false;
	});

	return (
		<NumericFormat
			customInput={TextField}
			value={localValue ?? ""}
			decimalScale={decimalScale}
			thousandSeparator
			onValueChange={(values) => {
				const newValue = values.floatValue ?? null;
				isDirtyRef.current = true;
				setLocalValue(newValue);
				debouncedOnChange(newValue);
			}}
			label={label}
			error={!!error}
			helperText={error}
			disabled={disabled ?? element.disabled}
			required={element.required}
			fullWidth
			className={judoClassName}
			data-testid={testId}
			slotProps={{
				input: {
					readOnly: readOnly ?? element.isReadOnly,
					startAdornment,
				},
				inputLabel: {
					shrink: true,
				},
			}}
		/>
	);
}
